import { venueChoiceForName, type VenueChoice } from "./tasting";
import { addDays, weekdayOfCivilDate, zonedLocalToUtc } from "./time";

export const HOUSE_GROUPS = [
  { key: "casa", label: "CASA" },
  { key: "ktema", label: "ΚΤΗΜΑ" },
  { key: "regina", label: "REGINA" },
] as const;

const FLOW_ORDER = ["sales", "tour", "food", "dessert", "chef"] as const;

export function tastingGroup(venueChoice: string | null | undefined, venueOther: string | null | undefined, venueName: string | null | undefined): { key: string; label: string } {
  const choice = knownChoice(venueChoice) ?? venueChoiceForName(venueName)?.choice ?? null;
  const house = HOUSE_GROUPS.find((group) => group.key === choice);
  if (house) return { key: house.key, label: house.label };
  const label = ((choice === "other" ? venueOther : null) ?? "").trim() || (venueName ?? "").trim() || "Άλλος χώρος";
  return { key: `other:${label}`, label };
}

export function houseGroups<T>(visits: { groupKey: string; groupLabel: string; visit: T }[]): { key: string; label: string; visits: T[] }[] {
  const buckets = new Map<string, { key: string; label: string; visits: T[] }>();
  for (const house of HOUSE_GROUPS) buckets.set(house.key, { key: house.key, label: house.label, visits: [] });
  for (const row of visits) {
    const bucket = buckets.get(row.groupKey) ?? { key: row.groupKey, label: row.groupLabel, visits: [] };
    bucket.visits.push(row.visit);
    buckets.set(row.groupKey, bucket);
  }
  const houses = HOUSE_GROUPS.map((house) => buckets.get(house.key)!);
  const rest = [...buckets.values()].filter((bucket) => !HOUSE_GROUPS.some((house) => house.key === bucket.key));
  rest.sort((left, right) => left.label.localeCompare(right.label, "el"));
  return [...houses, ...rest];
}

/** The tasting arrival clock, or the first recorded stage time when arrival is empty. */
export function tastingClock(arrival: string | null | undefined, flow: unknown): string | null {
  const time = (arrival ?? "").trim();
  if (/^\d{2}:\d{2}$/.test(time)) return time;
  return firstFlowTime(flow);
}

export function firstFlowTime(flow: unknown): string | null {
  if (!flow || typeof flow !== "object" || Array.isArray(flow)) return null;
  const record = flow as Record<string, unknown>;
  for (const key of FLOW_ORDER) {
    const value = record[key];
    if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) return value;
  }
  return null;
}

/** UTC timestamp shown as an Athens date and clock, `YYYY-MM-DD HH:MM`. */
export function displayStamp(value: string | null | undefined, timeZone = "Europe/Athens"): string {
  const text = (value ?? "").trim();
  if (!text) return "";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text.slice(0, 16).replace("T", " ");
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? "";
  let hour = part("hour");
  if (hour === "24") hour = "00";
  return `${part("year")}-${part("month")}-${part("day")} ${hour.slice(-2)}:${part("minute")}`;
}

export function displayClock(value: string | null | undefined, timeZone = "Europe/Athens"): string | null {
  const text = (value ?? "").trim();
  if (!text) return null;
  if (/^\d{2}:\d{2}$/.test(text)) return text;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(date);
  let hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  if (hour === "24") hour = "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour.slice(-2)}:${minute}`;
}

function knownChoice(value: string | null | undefined): VenueChoice | null {
  if (value === "casa" || value === "ktema" || value === "regina" || value === "other") return value;
  return null;
}

export type DateBounds = { from: string | null; to: string | null };

export type DatePreset = "today" | "week" | "month" | "year" | "all";

const ATHENS = "Europe/Athens";

export function parseCivilDate(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return value;
}

/** Monday–Sunday in Athens for the week that contains `today`. */
export function presetRange(preset: DatePreset, today: string): DateBounds {
  if (preset === "all") return { from: null, to: null };
  if (preset === "today") return { from: today, to: today };
  if (preset === "week") {
    const monday = addDays(today, -((weekdayOfCivilDate(today) + 6) % 7));
    return { from: monday, to: addDays(monday, 6) };
  }
  if (preset === "month") {
    const [year, month] = today.split("-");
    const last = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
    return { from: `${year}-${month}-01`, to: `${year}-${month}-${String(last).padStart(2, "0")}` };
  }
  const year = today.slice(0, 4);
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

export function dateClause(column: string, bounds: DateBounds): { sql: string; params: string[] } {
  const parts: string[] = [];
  const params: string[] = [];
  if (bounds.from) {
    parts.push(`${column} >= ?`);
    params.push(bounds.from);
  }
  if (bounds.to) {
    parts.push(`${column} <= ?`);
    params.push(bounds.to);
  }
  return { sql: parts.join(" AND "), params };
}

/** Inclusive civil dates become Athens midnights. `to` is exclusive at the next local midnight. */
export function timestampClause(column: string, bounds: DateBounds): { sql: string; params: string[] } {
  const parts: string[] = [];
  const params: string[] = [];
  if (bounds.from) {
    parts.push(`${column} >= ?`);
    params.push(zonedLocalToUtc(bounds.from, "00:00:00", ATHENS).toISOString());
  }
  if (bounds.to) {
    parts.push(`${column} < ?`);
    params.push(zonedLocalToUtc(addDays(bounds.to, 1), "00:00:00", ATHENS).toISOString());
  }
  return { sql: parts.join(" AND "), params };
}

export function depositPredicate(bounds: DateBounds): { sql: string; params: string[] } {
  const dated = ["length(json_extract(e.workbook_json, '$.deposit_date')) = 10"];
  const params: string[] = [];
  if (bounds.from) {
    dated.push("json_extract(e.workbook_json, '$.deposit_date') >= ?");
    params.push(bounds.from);
  }
  if (bounds.to) {
    dated.push("json_extract(e.workbook_json, '$.deposit_date') <= ?");
    params.push(bounds.to);
  }
  const created = timestampClause("e.created_at", bounds);
  const missing = "(json_extract(e.workbook_json, '$.deposit_date') IS NULL OR length(json_extract(e.workbook_json, '$.deposit_date')) != 10)";
  const fallback = created.sql ? `${missing} AND ${created.sql}` : missing;
  return { sql: `((${dated.join(" AND ")}) OR (${fallback}))`, params: [...params, ...created.params] };
}
